'use strict';
import React, { useState, useRef, useEffect } from 'react';
import { Icons } from './Icons';
import { useTree, useTreePeople } from '../contexts/TreeContext';
import { track } from '../utils/analytics';

const inputStyle = {
  width: '100%', padding: '10px 12px', borderRadius: 8,
  border: '1.5px solid #D5CAB8', background: '#fff',
  fontSize: 13, color: '#2C2418', outline: 'none',
  fontFamily: "'DM Sans', sans-serif", boxSizing: 'border-box',
};

const labelStyle = {
  fontSize: 12, fontWeight: 600, color: '#6B5E4F',
  display: 'block', marginBottom: 6,
  textTransform: 'uppercase', letterSpacing: 0.8,
};

const RELATION_META = {
  parent:  { label: 'Parent',  verb: 'Add Parent for',  icon: '↑' },
  child:   { label: 'Child',   verb: 'Add Child for',   icon: '↓' },
  spouse:  { label: 'Spouse',  verb: 'Add Spouse for',  icon: '⚭' },
};

export default function AddRelatedPersonModal({ relationshipType, anchorPerson, secondParentId, onClose }) {
  const { addPerson, batchUpdatePeople } = useTree();
  const { people } = useTreePeople();

  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName]   = useState('');
  const [birth, setBirth]         = useState('');
  const [gender, setGender]       = useState(
    relationshipType === 'spouse'
      ? (anchorPerson?.gender === 'M' ? 'F' : anchorPerson?.gender === 'F' ? 'M' : 'M')
      : 'M'
  );
  const [saving, setSaving] = useState(false);

  const firstNameRef = useRef(null);
  useEffect(() => { firstNameRef.current?.focus(); }, []);

  const meta = RELATION_META[relationshipType];
  const anchorId = anchorPerson?.id;

  const handleSave = async () => {
    if (!firstName.trim()) return;
    setSaving(true);

    const lastName_ = lastName.trim();
    const name = lastName_ ? `${firstName.trim()} ${lastName_}` : firstName.trim();

    const newPersonData = {
      name,
      birth: birth || null,
      death: null,
      birthPlace: null,
      deathPlace: null,
      occupation: null,
      gender,
      parentIds: [],
      marriages: [],
      children: [],
      photoURL: null,
    };

    // Each branch below creates the person with no relationships, then writes
    // both halves of the link in one batch. They used to be two or three
    // separate calls, so an interruption between them left the relationship
    // recorded on one document and not the other — a marriage on the spouse but
    // not on the person, a parent link the parent did not know about. Creating
    // unlinked first is also what the server's contributor rules expect of this
    // flow (is_unlinked in api/authz.py), and batch_update_people authorizes
    // every target against pre-batch state, so the new person is still unlinked
    // when the batch is checked.
    if (relationshipType === 'parent') {
      // New person is a parent of anchorPerson
      const newParentId = await addPerson(newPersonData);
      await batchUpdatePeople([
        { personId: anchorId, data: { parentIds: [...new Set([...(anchorPerson.parentIds || []), newParentId])] } },
        { personId: newParentId, data: { children: [anchorId] } },
      ]);

    } else if (relationshipType === 'child') {
      // Build parentIds: anchor plus optional second parent (from a marriage card)
      const parentIds = secondParentId ? [anchorId, secondParentId] : [anchorId];
      const newChildId = await addPerson(newPersonData);
      const updates = [
        { personId: newChildId, data: { parentIds } },
        { personId: anchorId, data: { children: [...new Set([...(anchorPerson.children || []), newChildId])] } },
      ];
      // Second parent, when the add came from a marriage card
      if (secondParentId && people[secondParentId]) {
        updates.push({
          personId: secondParentId,
          data: { children: [...new Set([...(people[secondParentId].children || []), newChildId])] },
        });
      }
      await batchUpdatePeople(updates);

    } else if (relationshipType === 'spouse') {
      // New person is a spouse of anchorPerson
      const marriageEntry = {
        marriageDate: null,
        marriagePlace: null,
        status: 'current',
        endReason: null,
        endDate: null,
        children: [],
      };
      const newSpouseId = await addPerson(newPersonData);
      await batchUpdatePeople([
        { personId: anchorId, data: { marriages: [...(anchorPerson.marriages || []), { spouseId: newSpouseId, ...marriageEntry }] } },
        { personId: newSpouseId, data: { marriages: [{ spouseId: anchorId, ...marriageEntry }] } },
      ]);
    }

    // One event for the link, after every write behind it has landed —
    // relationshipType is the whole point of the event, and this is the only
    // place in the app that still knows which kind was asked for.
    track('relationship_added', { type: relationshipType, via: 'add_related' });

    setSaving(false);
    onClose();
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && firstName.trim()) handleSave();
    if (e.key === 'Escape') onClose();
  };

  return (
    <div
      style={{
        position: 'fixed', inset: 0, background: 'rgba(44,36,24,0.5)', zIndex: 200,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        backdropFilter: 'blur(4px)',
      }}
      onClick={e => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div style={{
        width: 420, background: '#FDFBF7', borderRadius: 16,
        boxShadow: '0 20px 60px rgba(0,0,0,0.22)', overflow: 'hidden',
        fontFamily: "'DM Sans', sans-serif",
      }}>
        {/* Header */}
        <div style={{
          padding: '18px 22px', borderBottom: '1px solid #E8E0D4',
          display: 'flex', justifyContent: 'space-between', alignItems: 'center',
          background: '#F5EDE3',
        }}>
          <div>
            <p style={{ margin: 0, fontSize: 11, fontWeight: 700, color: '#C4975A', textTransform: 'uppercase', letterSpacing: 1 }}>
              {meta.verb}
            </p>
            <h3 style={{ margin: '2px 0 0', fontSize: 17, fontFamily: "'Crimson Pro', Georgia, serif", color: '#2C2418', fontWeight: 700 }}>
              {secondParentId && people[secondParentId]
                ? `${anchorPerson?.name} & ${people[secondParentId].name}`
                : anchorPerson?.name}
            </h3>
          </div>
          <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#8B7D6B', padding: 4 }}>
            <Icons.Close />
          </button>
        </div>

        {/* Form */}
        <div style={{ padding: 22, display: 'flex', flexDirection: 'column', gap: 16 }} onKeyDown={handleKeyDown}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <div>
              <label style={labelStyle}>First Name *</label>
              <input
                ref={firstNameRef}
                type="text" value={firstName} onChange={e => setFirstName(e.target.value)}
                placeholder="e.g. John" style={inputStyle}
              />
            </div>
            <div>
              <label style={labelStyle}>Last Name</label>
              <input
                type="text" value={lastName} onChange={e => setLastName(e.target.value)}
                placeholder="e.g. Mitchell" style={inputStyle}
              />
            </div>
          </div>

          <div>
            <label style={labelStyle}>Date of Birth</label>
            <input
              type="text" value={birth} onChange={e => setBirth(e.target.value)}
              placeholder="YYYY-MM-DD" style={inputStyle}
            />
          </div>

          <div>
            <label style={labelStyle}>Gender</label>
            <div style={{ display: 'flex', gap: 10 }}>
              {[['M', 'Male'], ['F', 'Female'], ['O', 'Other']].map(([v, l]) => (
                <button key={v} onClick={() => setGender(v)} style={{
                  flex: 1, padding: '9px 0', borderRadius: 8,
                  border: gender === v ? '2px solid #C4975A' : '1.5px solid #D5CAB8',
                  background: gender === v ? '#F5EDE3' : '#fff',
                  color: gender === v ? '#C4975A' : '#6B5E4F',
                  fontSize: 13, fontWeight: 600, cursor: 'pointer',
                  fontFamily: "'DM Sans', sans-serif",
                }}>{l}</button>
              ))}
            </div>
          </div>

          <p style={{ margin: 0, fontSize: 12, color: '#A0937F' }}>
            You can fill in more details after adding.
          </p>
        </div>

        {/* Footer */}
        <div style={{
          padding: '14px 22px', borderTop: '1px solid #E8E0D4',
          display: 'flex', justifyContent: 'flex-end', gap: 10,
        }}>
          <button onClick={onClose} style={{
            padding: '9px 22px', borderRadius: 8, border: '1.5px solid #D5CAB8',
            background: '#fff', color: '#6B5E4F', fontSize: 13, fontWeight: 600,
            cursor: 'pointer', fontFamily: "'DM Sans', sans-serif",
          }}>Cancel</button>
          <button
            onClick={handleSave}
            disabled={saving || !firstName.trim()}
            style={{
              padding: '9px 26px', borderRadius: 8, border: 'none',
              background: saving || !firstName.trim() ? '#B8AFA0' : '#3B2F1E',
              color: '#F5EDE3', fontSize: 13, fontWeight: 600,
              cursor: saving || !firstName.trim() ? 'not-allowed' : 'pointer',
              fontFamily: "'DM Sans', sans-serif",
            }}
          >
            {saving ? 'Saving…' : `Add ${meta.label}`}
          </button>
        </div>
      </div>
    </div>
  );
}
